import { Alert } from "@mui/material";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  AdminListRequestOfDescendantContentBlockFilter,
  DescendantContentBlockFilter,
  AdminContentItem,
  ListResponseOfDescendantContentBlock,
} from "services/KnowfullyAdminClient";
import useGlobalContext from "hooks/globalData";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  LazyTableRow,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import ContentBlockValue from "./ContentBlockValue";
import contextStore from "stores/contextStore";

type DescendantContentBlocksTableProps = {
  contentBlock: AdminContentItem;
};

const DescendantContentBlocksTable = ({
  contentBlock,
}: DescendantContentBlocksTableProps) => {
  const { formatMessage: t } = useIntl();
  const { contentService } = useGlobalContext();
  const { organizationId } = contextStore.useState();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("organizationName");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const contentBlockFilter = DescendantContentBlockFilter.fromJS({
    contentBlockName: contentBlock.name,
  });

  const filter = AdminListRequestOfDescendantContentBlockFilter.fromJS({
    pageNumber: page,
    pageSize: rowsPerPage,
    parameters: contentBlockFilter,
    sortField: orderBy,
    sortDescending: order === "desc",
  });

  const fetchContentBlocks = () =>
    contentService.getDescendantContentBlocks(filter);

  const { isError, data, error, isFetching } = useQuery<
    ListResponseOfDescendantContentBlock
  >(
    [
      "contentService.getDescendantContentBlocks",
      contentBlock.name,
      organizationId,
      order,
      orderBy,
      page,
      rowsPerPage,
    ],
    fetchContentBlocks,
    {
      keepPreviousData: true,
    }
  );

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const cells: Array<TableCellType> = [
    {
      accessor: "organizationName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "childOrganization" }),
    },
    {
      accessor: "value",
      align: "left",
      width: 0,
      type: "node",
      label: t({ id: "valueLabel" }),
      View: ContentBlockValue,
    },
    {
      accessor: "inheritedOrganizationName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "settingInheritedFromLabel" }),
      noSort: true,
    },
  ];

  interface IDescendantContentBlockRow extends LazyTableRow {
    id: string;
    organizationName: string;
    value: string;
    inheritedOrganizationName: string;
  }

  const rawSummary = (data?.data ?? []).map(item => ({
    ...item,
    id: item.organizationName ?? '', // Use organizationName as the unique id
  })) as IDescendantContentBlockRow[];

  return (
    <LazyTable
      {...{
        rowCount: data?.totalRecords ?? 0,
        rows: rawSummary ?? [],
        loading: isFetching,
        dense: true,
        cells,
        page,
        setPage,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        rowsPerPage,
        setRowsPerPage,
        collapsable: false,
        allowDragAndDrop: false,
      }}
    />
  );
};

export default DescendantContentBlocksTable;
